import { NetlifyLogsButton } from '~/components/ui/NetlifyLogsButton';
import type { ErrorDisplayProps } from '~/lib/types/netlify';
import { Tooltip } from '~/components/ui/Tooltip';
import { useTranslation } from '~/lib/hooks/useTranslation';

export const ErrorDisplay = ({
  error,
  adminUrl,
  onFixWithDualite,
  deployedId,
  accessToken,
  deployStatus,
  isErrorFixable,
  hasExistingDeployment = false,
}: ErrorDisplayProps) => {
  const { t, ready } = useTranslation(false);

  if (!ready) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div
          className={`rounded-md border border-dualite-border-secondary bg-dualite-background-button p-2 ${!adminUrl || hasExistingDeployment ? 'w-full' : 'flex-1'}`}
        >
          <div className="flex items-start gap-3 py-1">
            <div className="i-ph:warning-circle-fill w-5 h-5 text-dualite-text-modal-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-dualite-text-modal-primary text-sm font-medium mb-1">{t('deployment.failed')}</p>
              <p className="text-dualite-text-modal-secondary text-xs">
                {error?.includes('rate limit') ? (
                  <>
                    {t('deployment.rateLimitReached')}
                    <br />
                    <span className="text-xs opacity-80 mt-1 block">{t('deployment.rateLimitNote')}</span>
                  </>
                ) : (
                  error
                )}
              </p>
            </div>
          </div>
        </div>
        {adminUrl && !hasExistingDeployment && (
          <Tooltip
            content={t('modals.deploy.inspectDeployment')}
            className="h-[58px] w-[58px] flex items-center justify-center"
          >
            <button
              onClick={() => window.open(adminUrl ?? '', '_blank')}
              className="h-full w-full rounded-md border border-dualite-border-secondary bg-transparent 
          hover:bg-dualite-background-button transition-all duration-200
          text-dualite-text-modal-secondary hover:text-dualite-text-modal-primary flex items-center justify-center"
            >
              <div className="i-ph:gear w-5 h-5 shrink-0" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Add NetlifyLogsButton for error state */}
      {deployedId && (
        <div className="mt-2">
          <NetlifyLogsButton
            deployedId={deployedId}
            accessToken={accessToken}
            deployStatus={deployStatus}
            autoExpand={false}
            adminUrl={adminUrl}
          />
        </div>
      )}
      {/* Fix with Dualite button */}
      {!isErrorFixable ? (
        <button
          id="visit-netlify-dashboard-button"
          onClick={() => window.open('https://app.netlify.com', '_blank')}
          className="w-full px-4 py-2.5 bg-dualite-background-cta 
          rounded-md text-dualite-text-cta text-sm font-semibold hover:bg-dualite-background-cta-hover transition-all
          flex items-center justify-center gap-2 shadow-sm"
        >
          <div className="i-ph:wrench-fill w-4 h-4" />
          <span>{t('modals.deploy.visitNetlifyDashboard')}</span>
        </button>
      ) : (
        <button
          id="fix-with-dualite-button"
          onClick={onFixWithDualite}
          className="w-full px-4 py-2.5 bg-dualite-background-cta 
          rounded-md text-dualite-text-cta text-sm font-semibold hover:bg-dualite-background-cta-hover transition-all
          flex items-center justify-center gap-2 shadow-sm"
        >
          <div className="i-ph:wrench-fill w-4 h-4" />
          <span>{t('modals.deploy.fixWithDualite')}</span>
        </button>
      )}
    </div>
  );
};
